/**
 * Preference type → strategy CDN icon paths.
 *
 * Belongs here: PreferenceTypeId ↔ Strategy CDN path (exact + product aliases).
 * Does not belong here: guessing icons for unrelated ids (use OTHER_EXCLUDED fallback).
 *
 * Strategy assets: children, debt, elss, emergency, global, gold, high/medium/low risk,
 * hybrid, index, new_investment, other_excluded, other_included, retirement, sectoral.
 *
 * Do not map SATELLITE → sectoral (unrelated).
 */
import { PreferenceTypeId, CORE_PREFERENCE_TYPE_IDS } from './types'

/** Hardcoded strategy icon asset paths (CDN-relative). */
export const PreferenceStrategyIconPath = {
  CHILDREN: '/icons/strategy/ic_children.svg',
  DEBT: '/icons/strategy/ic_debt.svg',
  ELSS: '/icons/strategy/ic_elss.svg',
  EMERGENCY: '/icons/strategy/ic_emergency.svg',
  GLOBAL: '/icons/strategy/ic_global.svg',
  GOLD: '/icons/strategy/ic_gold.svg',
  HIGH_RISK: '/icons/strategy/ic_high_risk.svg',
  HYBRID: '/icons/strategy/ic_hybrid.svg',
  INDEX: '/icons/strategy/ic_index.svg',
  LOW_RISK: '/icons/strategy/ic_low_risk.svg',
  MEDIUM_RISK: '/icons/strategy/ic_medium_risk.svg',
  NEW_INVESTMENT: '/icons/strategy/ic_new_investment.svg',
  OTHER_EXCLUDED: '/icons/strategy/ic_other_excluded.svg',
  OTHER_INCLUDED: '/icons/strategy/ic_other_included.svg',
  RETIREMENT: '/icons/strategy/ic_retirement.svg',
  SECTORAL: '/icons/strategy/ic_sectoral.svg'
} as const

/**
 * PreferenceTypeId → strategy path. Unlisted ids use OTHER_EXCLUDED via getter.
 *
 * Exact: CHILDREN, DEBT, EMERGENCY, HIGH/MEDIUM/LOW_RISK, HYBRID, INDEX, NEW_INVESTMENT,
 * RETIREMENT, SECTORAL, OTHER → other_excluded.
 *
 * Product aliases (id name ≠ asset name):
 * SILVER_AND_GOLD → gold, GLOBAL_US / GLOBAL_NON_CORE / GLOBAL_OTHER → global,
 * DEBT_AND_ARBITRAGE → debt, ELSS_TAX_SAVINGS → elss, OTHERS → other_excluded.
 *
 * Unmapped (fallback): SATELLITE, CORE, GOAL_BASED, fund categories, etc.
 * OTHER_INCLUDED is not used as a type icon (include/exclude state).
 */
const STRATEGY_ICON_PATH_BY_PREFERENCE_TYPE_ID: Partial<Record<PreferenceTypeId, string>> = {
  [PreferenceTypeId.CHILDREN]: PreferenceStrategyIconPath.CHILDREN,
  [PreferenceTypeId.DEBT]: PreferenceStrategyIconPath.DEBT,
  [PreferenceTypeId.DEBT_AND_ARBITRAGE]: PreferenceStrategyIconPath.DEBT, // alias
  [PreferenceTypeId.ELSS_TAX_SAVINGS]: PreferenceStrategyIconPath.ELSS, // alias
  [PreferenceTypeId.EMERGENCY]: PreferenceStrategyIconPath.EMERGENCY,
  [PreferenceTypeId.GLOBAL_US]: PreferenceStrategyIconPath.GLOBAL, // alias
  [PreferenceTypeId.GLOBAL_NON_CORE]: PreferenceStrategyIconPath.GLOBAL, // alias
  [PreferenceTypeId.GLOBAL_OTHER]: PreferenceStrategyIconPath.GLOBAL, // alias
  [PreferenceTypeId.SILVER_AND_GOLD]: PreferenceStrategyIconPath.GOLD, // alias
  [PreferenceTypeId.HIGH_RISK]: PreferenceStrategyIconPath.HIGH_RISK,
  [PreferenceTypeId.HYBRID]: PreferenceStrategyIconPath.HYBRID,
  [PreferenceTypeId.INDEX]: PreferenceStrategyIconPath.INDEX,
  [PreferenceTypeId.LOW_RISK]: PreferenceStrategyIconPath.LOW_RISK,
  [PreferenceTypeId.MEDIUM_RISK]: PreferenceStrategyIconPath.MEDIUM_RISK,
  [PreferenceTypeId.NEW_INVESTMENT]: PreferenceStrategyIconPath.NEW_INVESTMENT,
  [PreferenceTypeId.OTHER]: PreferenceStrategyIconPath.OTHER_EXCLUDED,
  [PreferenceTypeId.OTHERS]: PreferenceStrategyIconPath.OTHER_EXCLUDED, // alias

  [PreferenceTypeId.RETIREMENT]: PreferenceStrategyIconPath.RETIREMENT,
  [PreferenceTypeId.SECTORAL]: PreferenceStrategyIconPath.SECTORAL
}

export function isNonCorePreferenceType(preferenceTypeId: PreferenceTypeId | string): boolean {
  return !CORE_PREFERENCE_TYPE_IDS.has(preferenceTypeId as PreferenceTypeId)
}

export function getQuestionOptionIds(question: {
  options?: Array<{ id?: string }> | null
}): string[] {
  const optionIds: string[] = []
  for (const option of question.options ?? []) {
    const optionId = option?.id ?? ''
    if (optionId !== '') optionIds.push(optionId)
  }
  return optionIds
}

/** CDN-relative strategy icon path (OTHER_EXCLUDED when PreferenceTypeId is unmapped). */
export function getPreferenceTypeStrategyIconPath(
  preferenceTypeId: PreferenceTypeId | string
): string {
  return (
    STRATEGY_ICON_PATH_BY_PREFERENCE_TYPE_ID[preferenceTypeId as PreferenceTypeId] ??
    PreferenceStrategyIconPath.OTHER_EXCLUDED
  )
}
